import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { emptyQuizDraft, quizFormToDraft } from "./quizDraftDefaults";

const QuizDraftContext = createContext(null);
QuizDraftContext.displayName = "QuizDraftContext";

export function QuizDraftProvider({ children }) {
  const [draft, setDraftState] = useState(emptyQuizDraft);
  const [uiActions, setUiActions] = useState(null);
  const [lastCreatedQuiz, setLastCreatedQuiz] = useState(null);
  const [formOpenRequest, setFormOpenRequest] = useState(0);
  const syncingFromFormRef = useRef(false);

  const setDraft = useCallback((next) => {
    setDraftState((prev) => {
      const resolved = typeof next === "function" ? next(prev) : next;
      return { ...emptyQuizDraft(), ...resolved };
    });
  }, []);

  const applyCopilotResult = useCallback((result) => {
    if (result?.draft) setDraft(result.draft);
    if (result?.ui_actions) setUiActions(result.ui_actions);
    if (result?.ui_actions?.open_quiz_form) {
      setFormOpenRequest((n) => n + 1);
    }
    if (result?.quiz) setLastCreatedQuiz(result.quiz);
  }, [setDraft]);

  const syncFromManualForm = useCallback((form) => {
    setDraftState((prev) => {
      const next = quizFormToDraft(form, prev);
      const sameCore =
        prev.title === next.title &&
        Number(prev.time_limit_minutes) === Number(next.time_limit_minutes) &&
        String(prev.rules_html || "") === String(next.rules_html || "") &&
        JSON.stringify(prev.topic_rules || []) === JSON.stringify(next.topic_rules || []);
      if (sameCore) return prev;
      syncingFromFormRef.current = true;
      queueMicrotask(() => {
        syncingFromFormRef.current = false;
      });
      if (prev.awaiting_confirmation) {
        next.awaiting_confirmation = false;
        next.confirmation_fingerprint = null;
      }
      return next;
    });
  }, []);

  const clearDraft = useCallback(() => {
    setDraftState(emptyQuizDraft());
    setUiActions(null);
  }, []);

  const value = useMemo(
    () => ({
      draft,
      setDraft,
      uiActions,
      setUiActions,
      lastCreatedQuiz,
      setLastCreatedQuiz,
      formOpenRequest,
      applyCopilotResult,
      syncFromManualForm,
      clearDraft,
      isSyncingFromForm: () => syncingFromFormRef.current,
    }),
    [
      draft,
      setDraft,
      uiActions,
      lastCreatedQuiz,
      formOpenRequest,
      applyCopilotResult,
      syncFromManualForm,
      clearDraft,
    ]
  );

  return <QuizDraftContext.Provider value={value}>{children}</QuizDraftContext.Provider>;
}

QuizDraftProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useQuizDraft() {
  const ctx = useContext(QuizDraftContext);
  if (!ctx) throw new Error("useQuizDraft must be used within QuizDraftProvider");
  return ctx;
}

export function useQuizDraftOptional() {
  return useContext(QuizDraftContext);
}

export default QuizDraftProvider;
